'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Text, Icon, HStack, VStack, Spinner } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { BrainCircuit, CheckCircle2, Circle, FileText, Download, Play, Zap } from 'lucide-react';

/**
 * LargeContextThinkingDisplay - Displays the multi-step thinking process
 * for the large context synthesis agent that handles documents > 1M tokens.
 *
 * Shows a beautiful timeline of steps as they happen.
 */
export const LargeContextThinkingDisplay = ({
                                                isProcessing = true,
                                                onCitationClick,
                                                historyKey = 'large_context',
                                                celeryTaskId = null,
                                                progressHistory = [],
                                            }) => {
    // Keyframe animations
    const fadeInUp = keyframes`
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    `;

    const pulse = keyframes`
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    `;

    const shimmer = keyframes`
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
    `;

    const spin = keyframes`
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    `;

    const glow = keyframes`
        0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.5); }
        50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.8); }
    `;

    const [displayedSteps, setDisplayedSteps] = useState([]);
    const [animatingIndex, setAnimatingIndex] = useState(-1);
    const containerRef = useRef(null);
    const prevHistoryLengthRef = useRef(0);

    // Debug logging
    // console.log("[LargeContextThinkingDisplay] Render - isProcessing:", isProcessing, "progressHistory:", progressHistory?.length || 0, "items");
    // if (progressHistory?.length > 0) {
    //     console.log("[LargeContextThinkingDisplay] Latest history item:", progressHistory[progressHistory.length - 1]);
    // }

    // Process and deduplicate progress history
    const processedSteps = useMemo(() => {
        if (!progressHistory || progressHistory.length === 0) return [];

        const steps = [];
        const seenMessages = new Set();

        for (const item of progressHistory) {
            const message = item.message?.trim();
            if (!message) continue;

            // Skip generic "Processing..." messages unless it's the only one
            if (message === 'Processing...' && seenMessages.size > 0) continue;

            // Create a simplified key for deduplication
            const key = message.toLowerCase();
            if (seenMessages.has(key)) continue;

            seenMessages.add(key);
            steps.push({
                message: message,
                state: item.state,
                timestamp: item.timestamp,
                icon: getStepIcon(message, item.state),
                color: getStepColor(message, item.state),
            });
        }

        return steps;
    }, [progressHistory]);

    // Sync displayedSteps with processedSteps and animate new ones
    useEffect(() => {
        // console.log("[LargeContextThinkingDisplay] useEffect - processedSteps:", processedSteps.length, "displayedSteps:", displayedSteps.length);

        if (processedSteps.length === 0) {
            if (displayedSteps.length > 0) {
                setDisplayedSteps([]);
            }
            return;
        }

        let scrollTimeout;
        let animationTimeout;

        if (processedSteps.length > displayedSteps.length) {
            // New steps to show - animate the last one
            setAnimatingIndex(processedSteps.length - 1);
            setDisplayedSteps(processedSteps);

            // Scroll to bottom
            if (containerRef.current) {
                scrollTimeout = setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = containerRef.current.scrollHeight;
                    }
                }, 50);
            }

            // Clear animation after delay
            animationTimeout = setTimeout(() => setAnimatingIndex(-1), 500);
        } else if (processedSteps.length !== displayedSteps.length) {
            // Steps changed in some other way, just sync
            setDisplayedSteps(processedSteps);
        }

        // Cleanup timeouts on unmount or before next effect run
        return () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            if (animationTimeout) clearTimeout(animationTimeout);
        };
    }, [processedSteps]);

    // Reset when processing ends
    useEffect(() => {
        if (!isProcessing) {
            // Keep displaying steps for a moment after completion, then could clear
            console.log("[LargeContextThinkingDisplay] Processing ended, keeping steps visible");
        }
    }, [isProcessing]);

    // Helper function to get icon based on message content
    function getStepIcon(message, state) {
        const msg = message.toLowerCase();
        if (msg.includes('starting') || msg.includes('initializing')) return Play;
        if (msg.includes('download') || msg.includes('skipped')) return Download;
        if (msg.includes('read') || msg.includes('document') || msg.includes('token')) return FileText;
        if (msg.includes('session') || msg.includes('resuming')) return Zap;
        if (state === 'SUCCESS') return CheckCircle2;
        return Circle;
    }

    // Helper function to get color based on message content
    function getStepColor(message, state) {
        const msg = message.toLowerCase();
        if (state === 'STARTING') return 'blue';
        if (state === 'SUCCESS') return 'green';
        if (msg.includes('download') || msg.includes('skipped')) return 'purple';
        if (msg.includes('read') || msg.includes('token')) return 'teal';
        if (msg.includes('session') || msg.includes('resuming') || msg.includes('query')) return 'orange';
        return 'blue';
    }

    // Format timestamp to relative time
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        } catch {
            return '';
        }
    };

    // Don't render if not processing and no steps
    if (!isProcessing && displayedSteps.length === 0) return null;

    return (
        <Box
            bg="linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)"
            borderRadius="xl"
            p={4}
            position="relative"
            overflow="hidden"
            border="1px solid"
            borderColor="blue.200"
            boxShadow="0 4px 20px rgba(59, 130, 246, 0.15)"
            width="100%"
            maxW="100%"
        >
            {/* Animated top border */}
            <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                height="3px"
                background="linear-gradient(90deg, transparent, #3b82f6, #8b5cf6, #3b82f6, transparent)"
                backgroundSize="200% 100%"
                animation={isProcessing ? `${shimmer} 2s linear infinite` : 'none'}
            />

            {/* Header */}
            <HStack spacing={3} mb={4}>
                <Box
                    p={2}
                    borderRadius="lg"
                    bg="blue.500"
                    animation={isProcessing ? `${glow} 2s ease-in-out infinite` : 'none'}
                >
                    <Icon as={BrainCircuit} boxSize={5} color="white" />
                </Box>
                <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="md" fontWeight="700" color="gray.800">
                        Agent Mode Active
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        Processing your large document set
                    </Text>
                </VStack>
                {isProcessing && (
                    <Spinner size="sm" color="blue.500" thickness="2px" />
                )}
            </HStack>

            {/* Steps count */}
            {isProcessing && displayedSteps.length > 0 && (
                <Text fontSize="xs" color="blue.600" fontWeight="600" mb={3}>
                    {displayedSteps.length} {displayedSteps.length === 1 ? 'step' : 'steps'} completed
                </Text>
            )}

            {/* Timeline of steps */}
            <Box
                ref={containerRef}
                maxH="280px"
                overflowY="auto"
                pr={2}
                css={{
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#cbd5e1',
                        borderRadius: '4px',
                    },
                }}
            >
                <VStack spacing={0} align="stretch">
                    {displayedSteps.map((step, index) => {
                        const isLast = index === displayedSteps.length - 1;
                        const isAnimating = index === animatingIndex;
                        const StepIcon = step.icon;
                        const stepColor = step.color;

                        return (
                            <Box
                                key={`${step.timestamp}-${index}`}
                                position="relative"
                                pl={8}
                                pb={isLast ? 0 : 3}
                                animation={isAnimating ? `${fadeInUp} 0.3s ease-out` : 'none'}
                            >
                                {/* Timeline line */}
                                {!isLast && (
                                    <Box
                                        position="absolute"
                                        left="11px"
                                        top="24px"
                                        bottom={0}
                                        width="2px"
                                        bg={`${stepColor}.200`}
                                    />
                                )}

                                {/* Timeline dot/icon */}
                                <Box
                                    position="absolute"
                                    left={0}
                                    top="2px"
                                    width="24px"
                                    height="24px"
                                    borderRadius="full"
                                    bg={isLast && isProcessing ? `${stepColor}.500` : `${stepColor}.100`}
                                    border="2px solid"
                                    borderColor={`${stepColor}.${isLast ? '500' : '300'}`}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    animation={isLast && isProcessing ? `${pulse} 1.5s ease-in-out infinite` : 'none'}
                                >
                                    <Icon
                                        as={StepIcon}
                                        boxSize={3}
                                        color={isLast && isProcessing ? 'white' : `${stepColor}.500`}
                                    />
                                </Box>

                                {/* Step content */}
                                <Box
                                    bg={isLast ? 'white' : 'transparent'}
                                    p={isLast ? 3 : 1}
                                    borderRadius="lg"
                                    border={isLast ? '1px solid' : 'none'}
                                    borderColor="blue.100"
                                    boxShadow={isLast ? 'sm' : 'none'}
                                    transition="all 0.2s"
                                >
                                    <Text
                                        fontSize={isLast ? 'sm' : 'xs'}
                                        fontWeight={isLast ? '600' : '400'}
                                        color={isLast ? 'gray.800' : 'gray.600'}
                                        lineHeight="1.4"
                                    >
                                        {step.message}
                                    </Text>
                                    {step.timestamp && (
                                        <Text fontSize="2xs" color="gray.400" mt={0.5}>
                                            {formatTime(step.timestamp)}
                                        </Text>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </VStack>
            </Box>

            {/* Waiting indicator when no steps yet */}
            {isProcessing && displayedSteps.length === 0 && (
                <HStack justify="center" py={4}>
                    <Spinner size="sm" color="blue.500" />
                    <Text fontSize="sm" color="gray.500">
                        Initializing agent...
                    </Text>
                </HStack>
            )}

            {/* Processing indicator at bottom */}
            {isProcessing && displayedSteps.length > 0 && (
                <HStack justify="center" pt={3} mt={2} borderTop="1px solid" borderColor="blue.100">
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        {[0, 1, 2].map((i) => (
                            <Box
                                key={i}
                                w="6px"
                                h="6px"
                                bg="blue.400"
                                borderRadius="full"
                                animation={`${pulse} 1.4s ease-in-out infinite`}
                                style={{ animationDelay: `${i * 0.2}s` }}
                            />
                        ))}
                    </Box>
                    <Text fontSize="xs" color="gray.500" ml={2}>
                        Agent is working...
                    </Text>
                </HStack>
            )}
        </Box>
    );
};

export default LargeContextThinkingDisplay;
